<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  isSending: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['send']);

const { t } = useI18n();
const shortcutKey = useKbd(['$mod', '+', 'enter']);
const { isEditorHotKeyEnabled } = useUISettings();

const content = ref('');
const attachment = ref(null);
const fileInputRef = ref(null);

const canSend = computed(
  () =>
    (content.value.trim().length > 0 || attachment.value) && !props.isSending
);

const sendButtonLabel = computed(() => {
  if (props.isSending) return t('SUPPORT_CHAT.SENDING');
  const keyLabel = isEditorHotKeyEnabled('cmd_enter') ? shortcutKey.value : '↵';
  return `${t('SUPPORT_CHAT.SEND')} (${keyLabel})`;
});

const textareaPlaceholder = computed(() => {
  const hint = isEditorHotKeyEnabled('cmd_enter')
    ? t('SUPPORT_CHAT.ENTER_HINT')
    : t('SUPPORT_CHAT.SHIFT_ENTER_HINT');
  return `${t('SUPPORT_CHAT.PLACEHOLDER')} ${hint}`;
});

const openFilePicker = () => fileInputRef.value?.click();

const onFileSelected = event => {
  const [file] = event.target.files;
  attachment.value = file || null;
};

const removeAttachment = () => {
  attachment.value = null;
  if (fileInputRef.value) fileInputRef.value.value = null;
};

const submit = () => {
  if (!canSend.value) return;
  emit('send', { content: content.value.trim(), attachment: attachment.value });
};

const onEnterKeydown = event => {
  const modPressed = event.metaKey || event.ctrlKey;

  if (isEditorHotKeyEnabled('cmd_enter')) {
    // Cmd/Ctrl+Enter sends; plain Enter (with or without Shift) inserts a new line.
    if (!modPressed) return;
  } else if (event.shiftKey) {
    // Enter sends; Shift+Enter inserts a new line.
    return;
  }

  event.preventDefault();
  submit();
};

const clear = () => {
  content.value = '';
  removeAttachment();
};

defineExpose({ clear });
</script>

<template>
  <form
    class="flex flex-col gap-2 px-6 py-4 pr-20 border-t border-n-weak"
    @submit.prevent="submit"
  >
    <div
      v-if="attachment"
      class="flex items-center gap-2 text-xs text-n-slate-11 bg-n-slate-3 rounded-lg px-2 py-1 w-fit"
    >
      <span class="truncate max-w-[200px]">{{ attachment.name }}</span>
      <button type="button" @click="removeAttachment">
        <Icon icon="i-lucide-x" class="size-3" />
      </button>
    </div>

    <div class="flex items-center gap-2">
      <input
        ref="fileInputRef"
        type="file"
        class="hidden"
        @change="onFileSelected"
      />
      <Button
        type="button"
        icon="i-lucide-paperclip"
        color="slate"
        size="sm"
        class="!w-10"
        :disabled="isSending"
        @click="openFilePicker"
      />
      <textarea
        v-model="content"
        rows="1"
        class="flex-1 resize-none rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none"
        :placeholder="textareaPlaceholder"
        :disabled="isSending"
        @keydown.enter="onEnterKeydown"
      />
      <Button
        type="submit"
        :label="sendButtonLabel"
        :disabled="!canSend"
        :is-loading="isSending"
      />
    </div>
  </form>
</template>
